import { resourcesListingConfiguration } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveResourcesListingConfigurationRequest(
  state: State,
  planzone: Boundaries["retrieveResourcesListingConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          configuration: resourcesListingConfiguration,
        },
      ];
}

function handleRetrieveResourcesListingConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["retrieveResourcesListingConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleRetrieveResourcesListingConfigurationFailure(
  state: State,
  planzone: Boundaries["retrieveResourcesListingConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          configuration: resourcesListingConfiguration,
        },
      ];
}

function handleEditResourcesListingConfigurationRequest(
  state: State,
  {
    planzone,
    nextConfiguration,
  }: Boundaries["editResourcesListingConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration: nextConfiguration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration: nextConfiguration,
        },
      ];
}

function handleEditResourcesListingConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editResourcesListingConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleEditResourcesListingConfigurationFailure(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editResourcesListingConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveResourcesListingConfigurationRequested]:
    handleRetrieveResourcesListingConfigurationRequest,
  [events.retrieveResourcesListingConfigurationSucceeded]:
    handleRetrieveResourcesListingConfigurationSuccess,
  [events.retrieveResourcesListingConfigurationFailed]:
    handleRetrieveResourcesListingConfigurationFailure,
  [events.editResourcesListingConfigurationRequested]:
    handleEditResourcesListingConfigurationRequest,
  [events.editResourcesListingConfigurationSucceeded]:
    handleEditResourcesListingConfigurationSuccess,
  [events.editResourcesListingConfigurationFailed]:
    handleEditResourcesListingConfigurationFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
